import api from "@/lib/axios";
import { API_ENDPOINTS } from "@/constants/api-endpoints";
import type { VehicleAPIResponse, VehiclesListResponse } from "@/types";

export interface VehiclesParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export const vehiclesApi = {
  getAll: async (params: VehiclesParams): Promise<VehiclesListResponse> => {
    // Only send page/limit — backend may not support status/search yet
    const response = await api.get(API_ENDPOINTS.VEHICLES.LIST, {
      params: { page: params.page, limit: params.limit },
    });
    const raw: VehiclesListResponse = response.data?.data ?? response.data;

    // Client-side filter by status and search until backend supports it
    let vehicles = raw.vehicles ?? [];
    if (params.status && params.status !== 'all') {
      vehicles = vehicles.filter((v) => v.verification_status === params.status);
    }
    if (params.search) {
      const q = params.search.toLowerCase();
      vehicles = vehicles.filter(
        (v) =>
          v.vehicle_model?.toLowerCase().includes(q) ||
          v.registration_number?.toLowerCase().includes(q) ||
          v.driver?.user?.name?.toLowerCase().includes(q),
      );
    }

    return { ...raw, vehicles, total: vehicles.length };
  },

  getPending: async (): Promise<VehicleAPIResponse[]> => {
    // Some endpoints may return wrapped { data: ... } or just raw array. Adjust based on exact response.
    const response = await api.get(API_ENDPOINTS.VEHICLES.PENDING);
    // Based on convention, maybe returning raw array or wrapped
    return response.data.data || response.data;
  },

  getById: async (id: string): Promise<VehicleAPIResponse> => {
    const response = await api.get(API_ENDPOINTS.VEHICLES.DETAIL(id));
    return response.data?.data || response.data;
  },

  approve: async (id: string): Promise<void> => {
    await api.put(API_ENDPOINTS.VEHICLES.APPROVE(id));
  },

  reject: async (id: string, reason: string): Promise<void> => {
    await api.put(API_ENDPOINTS.VEHICLES.REJECT(id), { reason });
  },
};
